/**
 * One-Tap Signup for Shopify.
 *
 * Google confirms the visitor's email and name in the browser; we submit the
 * store's own newsletter form (/contact, form_type=customer) on their behalf.
 * Shopify creates the customer and records marketing consent, and whatever
 * email app is connected to the store picks them up from there. No backend,
 * no API keys.
 *
 * Two ways in, both landing on the same submit:
 *
 *  1. The styled button. It is a real <button> — ours — wired to Google's
 *     OAuth token client. Clicking it opens Google's account picker in a
 *     popup; we then read the profile from Google's userinfo endpoint.
 *     This is deliberately NOT Google's rendered "Sign in with Google"
 *     button hidden under ours: Google guards that iframe against
 *     clickjacking and silently drops clicks when it sits under an
 *     opacity-0 ancestor, and popups opened via its cross-frame messaging
 *     can lose the user gesture and be blocked. A button we own has neither
 *     problem.
 *
 *  2. Google's floating One Tap prompt (optional, off by default). That path
 *     hands back an ID token, which we decode for the same three fields.
 *
 * Nothing here is cryptographically verified. That needs a server, and it
 * would buy nothing: /contact is a public, unauthenticated form, so a forged
 * profile achieves nothing a plain POST could not. What Google gives us is an
 * address it has confirmed and nobody had to type.
 */
(function () {
	'use strict';

	if (window.__oneTapSignupBooted) {
		return;
	}
	window.__oneTapSignupBooted = true;

	var GSI_SRC = 'https://accounts.google.com/gsi/client';
	var USERINFO = 'https://www.googleapis.com/oauth2/v3/userinfo';
	var REMEMBER_KEY = 'one_tap_subscribed';
	var NAME_KEY = 'one_tap_name';

	var STRINGS = {
		loading: 'Loading Google…',
		working: 'Subscribing…',
		success: 'You’re subscribed.',
		unverified: 'Google hasn’t verified that address. Please use the form instead.',
		popupBlocked: 'Your browser blocked the Google window. Allow pop-ups for this site and try again.',
		error: 'Something went wrong. Please use the signup form instead.'
	};

	var tokenClient = null;
	var googleReady = false;

	function widgets() {
		return Array.prototype.slice.call(document.querySelectorAll('[data-one-tap]'));
	}

	function settings() {
		var first = widgets()[0];
		if (!first) {
			return null;
		}
		return {
			clientId: first.getAttribute('data-client-id') || '',
			locale: (first.getAttribute('data-locale') || 'en').slice(0, 2),
			prompt: first.getAttribute('data-prompt') === 'true'
		};
	}

	// --- Remembering a signup ---------------------------------------------

	function alreadySubscribed() {
		try {
			if (window.localStorage && localStorage.getItem(REMEMBER_KEY)) {
				return true;
			}
		} catch (e) {
			/* storage blocked; the cookie carries it */
		}
		return document.cookie.indexOf(REMEMBER_KEY + '=1') !== -1;
	}

	function remember() {
		try {
			localStorage.setItem(REMEMBER_KEY, '1');
		} catch (e) {
			/* fine */
		}
		document.cookie = REMEMBER_KEY + '=1; path=/; max-age=' + 60 * 60 * 24 * 365 + '; SameSite=Lax';
	}

	// --- UI ----------------------------------------------------------------

	function setStatus(message, state) {
		widgets().forEach(function (widget) {
			var el = widget.querySelector('[data-one-tap-status]');
			if (el) {
				el.textContent = message || '';
				el.setAttribute('data-state', state || '');
			}
		});
	}

	function hideControls() {
		widgets().forEach(function (widget) {
			['[data-one-tap-wrap]', '.one-tap__consent', '.one-tap__heading'].forEach(function (sel) {
				var el = widget.querySelector(sel);
				if (el) {
					el.hidden = true;
				}
			});
		});
	}

	function warn(message) {
		if (window.console && console.warn) {
			console.warn('[one-tap-signup] ' + message);
		}
	}

	// --- Profile from either flow ----------------------------------------------

	/** Read a JWT payload (One Tap prompt path). Not a signature check. */
	function decodePayload(token) {
		var parts = String(token || '').split('.');
		if (parts.length !== 3) {
			return null;
		}
		try {
			var b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
			while (b64.length % 4) {
				b64 += '=';
			}
			var bytes = atob(b64);
			var utf8 = decodeURIComponent(
				Array.prototype.map
					.call(bytes, function (c) {
						return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
					})
					.join('')
			);
			return JSON.parse(utf8);
		} catch (e) {
			return null;
		}
	}

	/** Both flows end here with { email, email_verified, given_name, family_name }. */
	function handleProfile(profile) {
		if (!profile || !profile.email) {
			setStatus(STRINGS.error, 'error');
			return;
		}
		if (profile.email_verified !== true && profile.email_verified !== 'true') {
			setStatus(STRINGS.unverified, 'error');
			return;
		}

		var form = widgets()[0].querySelector('form.one-tap__form');
		if (!form) {
			warn('The section did not render its {% form %} — the Liquid file may be out of date.');
			setStatus(STRINGS.error, 'error');
			return;
		}

		setStatus(STRINGS.working, 'working');

		// Carry the first name across the reload so the confirmation can use it.
		try {
			sessionStorage.setItem(NAME_KEY, profile.given_name || '');
		} catch (e) {
			/* fine, the confirmation just omits the name */
		}

		/*
		 * Fill Shopify's own form and submit it the way a button would.
		 *
		 * The form must be the one Liquid rendered, and the submit must go
		 * through requestSubmit() rather than submit(): with spam protection
		 * on, Shopify attaches an invisible reCAPTCHA token by intercepting the
		 * submit event on forms it rendered. submit() fires no event, and a
		 * form built in JavaScript was never Shopify's — either way the post
		 * arrives without a token and is refused with "Missing CAPTCHA token".
		 *
		 * This navigates. On success Shopify returns the browser here with
		 * customer_posted=true and boot() shows the confirmation; on any
		 * refusal the visitor sees Shopify's own page saying why.
		 */
		setField(form, '.one-tap__field-email', profile.email);
		setField(form, '.one-tap__field-first', profile.given_name || '');
		setField(form, '.one-tap__field-last', profile.family_name || '');

		submitShopifyForm(form);
	}

	function setField(form, selector, value) {
		var input = form.querySelector(selector);
		if (input) {
			input.value = value;
		}
	}

	// --- Shopify's spam protection ---------------------------------------------

	/*
	 * How Shopify's storefront captcha actually behaves (from its bootstrap):
	 *
	 *  - It binds hCaptcha to a protected form only when a visitor focuses or
	 *    changes a field inside it, or at page load if the form is marked
	 *    data-shopify-captcha="true".
	 *  - On submit, a protected form that is NOT yet bound gets
	 *    preventDefault() and nothing else. The submission is swallowed.
	 *
	 * This form has only hidden fields, so nothing ever focuses it, so it is
	 * never bound, so every submit vanishes into "Subscribing…". The fix is the
	 * API Shopify exposes for exactly this: Shopify.captcha.protect(form, cb)
	 * binds on demand and calls back once the provider is ready. We warm it up
	 * at boot and, at submit time, wait for the bound flag before firing.
	 */
	var CAPTCHA_TIMEOUT = 8000;

	function captchaApi() {
		var s = window.Shopify;
		return s && s.captcha && typeof s.captcha.protect === 'function' ? s.captcha : null;
	}

	function captchaBound(form) {
		return form.dataset.hcaptchaBound === 'true' ||
			form.dataset.recaptchaBound === 'true' ||
			form.dataset.oneTapBound === '1';
	}

	function protectOnce(form) {
		if (form.dataset.oneTapProtected) {
			return;
		}
		form.dataset.oneTapProtected = '1';
		try {
			captchaApi().protect(form, function () {
				form.dataset.oneTapBound = '1';
			});
		} catch (e) {
			warn('Shopify.captcha.protect threw: ' + (e && e.message ? e.message : e));
		}
	}

	function warmCaptcha() {
		if (!captchaApi()) {
			return;
		}
		widgets().forEach(function (widget) {
			var form = widget.querySelector('form.one-tap__form');
			if (form && !captchaBound(form)) {
				protectOnce(form);
			}
		});
	}

	function fireSubmit(form) {
		// requestSubmit fires the submit event the captcha provider listens for;
		// submit() fires no event and would post without a token.
		if (typeof form.requestSubmit === 'function') {
			form.requestSubmit();
		} else {
			form.submit();
		}
	}

	function submitShopifyForm(form) {
		if (!captchaApi() || captchaBound(form)) {
			fireSubmit(form);
			return;
		}

		protectOnce(form);

		var done = false;
		var poll = setInterval(function () {
			if (captchaBound(form)) {
				finish();
			}
		}, 100);
		var giveUp = setTimeout(function () {
			if (done) {
				return;
			}
			done = true;
			clearInterval(poll);
			// Better to let Shopify answer than to leave the visitor on
			// "Subscribing…" forever. Without a token Shopify will show its own
			// page saying so, which at least is honest and visible.
			warn('Shopify\'s captcha did not bind the form within ' + CAPTCHA_TIMEOUT / 1000 + 's — submitting without it.');
			form.submit();
		}, CAPTCHA_TIMEOUT);

		function finish() {
			if (done) {
				return;
			}
			done = true;
			clearInterval(poll);
			clearTimeout(giveUp);
			fireSubmit(form);
		}
	}

	function succeed(firstName) {
		setStatus(firstName ? firstName + ' — ' + STRINGS.success : STRINGS.success, 'success');
		hideControls();
		remember();
	}

	// --- Flow 1: our button + OAuth token client ---------------------------------

	function onToken(response) {
		if (!response || !response.access_token) {
			// Google puts consent-level failures here, not in error_callback.
			// access_denied with an unpublished consent screen is the classic.
			var reason = response && response.error
				? response.error + (response.error_description ? ' — ' + response.error_description : '')
				: 'no access token in the response';
			warn(
				'Google did not grant access: ' + reason + '. ' +
					'If the popup said "Access blocked", the OAuth consent screen is still in Testing — ' +
					'publish it (Google Cloud Console → OAuth consent screen → Publish app).'
			);
			setStatus(STRINGS.error, 'error');
			return;
		}
		fetch(USERINFO, { headers: { Authorization: 'Bearer ' + response.access_token } })
			.then(function (res) {
				if (!res.ok) {
					warn('Google userinfo returned HTTP ' + res.status + ' — the token was accepted but the profile lookup was refused.');
					return null;
				}
				return res.json();
			})
			.then(handleProfile)
			.catch(function (err) {
				warn('Google userinfo request failed: ' + (err && err.message ? err.message : err) + ' — a content blocker or CSP may be stopping googleapis.com.');
				setStatus(STRINGS.error, 'error');
			});
	}

	function onTokenError(err) {
		var type = err && err.type;
		if (type === 'popup_closed') {
			setStatus('', '');
			return;
		}
		if (type === 'popup_failed_to_open') {
			setStatus(STRINGS.popupBlocked, 'error');
			return;
		}
		warn('Google token request failed: ' + (type || 'unknown') + (err && err.message ? ' — ' + err.message : ''));
		setStatus(STRINGS.error, 'error');
	}

	function onButtonClick(event) {
		event.preventDefault();
		if (!googleReady || !tokenClient) {
			setStatus(STRINGS.loading, 'working');
			return;
		}
		setStatus('', '');
		// Synchronously inside the click, so the popup keeps the user gesture.
		tokenClient.requestAccessToken();
	}

	function bindButtons() {
		widgets().forEach(function (widget) {
			var button = widget.querySelector('[data-one-tap-trigger]');
			if (!button || button.getAttribute('data-one-tap-bound')) {
				return;
			}
			button.setAttribute('data-one-tap-bound', '1');
			button.addEventListener('click', onButtonClick);
		});
	}

	// --- Flow 2: One Tap prompt ------------------------------------------------

	function onCredential(response) {
		if (!response || !response.credential) {
			return;
		}
		handleProfile(decodePayload(response.credential));
	}

	// --- Google ----------------------------------------------------------------

	function start(cfg) {
		tokenClient = google.accounts.oauth2.initTokenClient({
			client_id: cfg.clientId,
			scope: 'openid email profile',
			callback: onToken,
			error_callback: onTokenError
		});
		googleReady = true;

		if (cfg.prompt && google.accounts.id) {
			google.accounts.id.initialize({
				client_id: cfg.clientId,
				callback: onCredential,
				auto_select: false,
				cancel_on_tap_outside: true,
				context: 'signup',
				ux_mode: 'popup'
			});
			google.accounts.id.prompt();
		}
	}

	function isReady() {
		return typeof google !== 'undefined' && google.accounts && google.accounts.oauth2;
	}

	/**
	 * Google's script may arrive late — optimisers that delay third-party
	 * JavaScript hold it until interaction — so wait for it rather than
	 * checking once and giving up.
	 */
	function whenGoogleReady(callback) {
		if (isReady()) {
			callback();
			return;
		}
		if (!document.querySelector('script[src^="' + GSI_SRC + '"]')) {
			var script = document.createElement('script');
			script.src = GSI_SRC;
			script.async = true;
			script.defer = true;
			document.head.appendChild(script);
		}

		var attempts = 0;
		var events = ['pointerdown', 'keydown', 'touchstart', 'scroll'];

		function finish() {
			clearInterval(timer);
			events.forEach(function (name) {
				window.removeEventListener(name, poke, true);
			});
		}
		function poke() {
			if (isReady()) {
				finish();
				callback();
			}
		}
		var timer = setInterval(function () {
			attempts++;
			if (isReady()) {
				finish();
				callback();
			} else if (attempts > 300) {
				finish();
				warn('Google Identity Services never loaded. Check for a script blocker or an app that delays third-party JavaScript.');
			}
		}, 100);
		events.forEach(function (name) {
			window.addEventListener(name, poke, true);
		});
	}

	// --- Boot ------------------------------------------------------------------

	function boot() {
		var cfg = settings();
		if (!cfg) {
			return;
		}
		if (!cfg.clientId) {
			warn('No Google client ID set on the One-Tap Signup section.');
			return;
		}

		if (window.location.search.indexOf('customer_posted=true') !== -1) {
			var name = '';
			try {
				name = sessionStorage.getItem(NAME_KEY) || '';
				sessionStorage.removeItem(NAME_KEY);
			} catch (e) {
				/* fine */
			}
			succeed(name);
			return;
		}
		if (alreadySubscribed()) {
			hideControls();
			return;
		}

		// Bind first so an early click shows "Loading" instead of doing nothing.
		bindButtons();
		// Ask Shopify to bind its captcha now, so it is ready long before the
		// visitor gets back from Google.
		warmCaptcha();
		whenGoogleReady(function () {
			start(cfg);
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', boot);
	} else {
		boot();
	}
})();
